import type { DerogationsDeFamille, NiveauAcces } from '@taki/ui';
import type { PorteApp, ShellAppEntry } from './types.js';

/**
 * L'ACCES VU DU SHELL : les REGLES PURES, et rien d'autre.
 *
 * LA SECURITE REELLE EST DANS LES RULES (`firebase/firestore.rules`). Ce module
 * ne fait que masquer une app dans une liste et traduire les choix de l'ecran
 * des utilisateurs en derogations par porte. Si ce fichier et les rules
 * divergent, ce sont les rules qui ont raison.
 *
 * AUCUN IMPORT A L'EXECUTION, ET C'EST LA CONTRAINTE QUI TIENT CE FICHIER.
 * Les tests du shell tournent en `node --test` sur le JS compile de `dist/`,
 * avec une doublure pour `@taki/ui` (du TypeScript, que Node ne charge pas).
 * Une fonction qui appellerait une valeur de `@taki/ui` obligerait donc cette
 * doublure a porter la VRAIE valeur, c'est-a-dire a ecrire le meme fait dans un
 * fichier de test. Les deux constantes ci-dessous sont reecrites ici pour cette
 * raison, avec le nom de leur source, et `tests/acces.test.mjs` les verifie.
 *
 * Le PORT (`AccesPort`, `useAccesPort`) et le calcul du niveau effectif
 * (`niveauEffectif`) vivent, eux, dans `@taki/ui` (`src/lib/acces.tsx`) : c'est
 * le gabarit qui en a besoin d'abord.
 */

/**
 * La clef d'une derogation qui vaut pour TOUTE une famille : `*`, exactement
 * comme `TYPE_JOKER` de `@taki/ui` et comme les rules. C'est aussi la clef des
 * documents sans `type`.
 */
export const CLE_FAMILLE = '*';

/**
 * Le plus permissif des deux niveaux. C'est l'ordre de `RANG` (@taki/ui),
 * enonce comme une regle plutot que recopie comme une table.
 *
 * A QUOI IL SERT : deux apps installees peuvent partager une porte (Projets
 * et Timesheet lisent toutes deux `projects/projet`) et recevoir deux choix
 * differents. Retenir le plus ferme couperait l'app la mieux dotee sans le
 * dire ; on retient donc le plus ouvert, et le panneau l'ecrit a l'ecran.
 */
export function plusPermissif(a: NiveauAcces, b: NiveauAcces): NiveauAcces {
  if (a === 'write' || b === 'write') return 'write';
  if (a === 'read' || b === 'read') return 'read';
  return 'none';
}

/** Ce qu'une fiche d'app (`taki.app.json`) declare de ses donnees. */
interface EntiteDeFiche {
  name: string;
  types?: readonly string[];
}

/** Le minimum qu'une entree d'app doit porter pour qu'on en derive des portes. */
interface PorteuseDePortes {
  id: string;
  portes?: readonly PorteApp[];
}

/**
 * LES PORTES D'UNE APP, DERIVEES DE SA FICHE, et le seul endroit qui les derive.
 *
 * Une PORTE est une famille du SDK plus, quand il y en a un, le `type` qui la
 * specialise : `{ name: 'assets', types: ['materiel'] }` donne UNE porte
 * `assets/materiel`, `{ name: 'contacts' }` (aucun type, cas de l'app Contact)
 * donne la porte `contacts` toute entiere. Une famille listee avec plusieurs
 * types donne autant de portes.
 *
 * Les doublons tombent : deux entrees de fiche qui nomment la meme famille et
 * le meme type ne sont qu'une porte, et l'ecran des utilisateurs afficherait
 * sinon deux lignes identiques.
 */
export function portesDeFiche(
  entities: readonly EntiteDeFiche[] | undefined,
): PorteApp[] {
  if (entities == null) return [];
  const vues = new Set<string>();
  const portes: PorteApp[] = [];
  const poser = (famille: string, type?: string): void => {
    const clef = type == null ? famille : `${famille}/${type}`;
    if (vues.has(clef)) return;
    vues.add(clef);
    portes.push(type == null ? { famille } : { famille, type });
  };
  for (const entite of entities) {
    const famille = entite?.name;
    if (typeof famille !== 'string' || famille === '') continue;
    const types = (entite.types ?? []).filter(
      (type): type is string => typeof type === 'string' && type !== '',
    );
    if (types.length === 0) {
      poser(famille);
      continue;
    }
    for (const type of types) poser(famille, type);
  }
  return portes;
}

/**
 * LES APPS AUXQUELLES L'UTILISATEUR A ACCES. PURE, testable sans React.
 *
 * Meme forme que `withoutDeprecatedApps` (appListing.ts), et pour la meme
 * raison : REND LA MEME REFERENCE quand rien n'est retire. Les appelants la
 * passent a des `useMemo` ; un `filter` inconditionnel fabriquerait un tableau
 * neuf a chaque rendu, donc un dock qui se recalcule sans arret.
 *
 * SANS FONCTION DE NIVEAU (port absent, ou fiche pas encore lue), RIEN N'EST
 * RETIRE : on ne fait jamais disparaitre une app sur une absence
 * d'information. Une icone en trop pendant une frame est un desagrement ; une
 * app disparue sans recours est une panne.
 */
export function withoutInaccessibleApps(
  entries: readonly ShellAppEntry[],
  niveauApp: ((appId: string) => NiveauAcces) | null | undefined,
): readonly ShellAppEntry[] {
  if (niveauApp == null) return entries;
  const ouvertes = entries.filter((entry) => niveauApp(entry.id) !== 'none');
  return ouvertes.length === entries.length ? entries : ouvertes;
}

/**
 * CE QUE L'ECRAN DES UTILISATEURS ENREGISTRE : les derogations PAR PORTE,
 * derivees des deux choix que le panneau propose.
 *
 * Les rules ne lisent QUE les portes ; le choix par app n'est qu'une
 * commodite d'ecran, et c'est ici qu'il se traduit.
 *
 * TROIS REGLES, DANS CET ORDRE :
 *  1. une derogation EXPLICITE par porte prime toujours : elle a ete posee a la
 *     main, sur cette porte-la, elle ne se fait pas ecraser par un choix d'app ;
 *  2. un choix par app pose son niveau sur CHACUNE de ses portes ;
 *  3. deux apps qui partagent une porte avec des choix differents : le plus
 *     PERMISSIF gagne (voir `plusPermissif`), et le panneau le dit.
 *
 * « Defaut du role » ne s'ecrit pas : c'est l'ABSENCE de clef. Le panneau
 * retire donc l'entree au lieu d'y poser une valeur, et cette fonction ne
 * fabrique jamais de clef pour une app sans choix.
 */
export function deriverPortes(
  apps: Readonly<Record<string, NiveauAcces>>,
  portesExplicites: Readonly<Record<string, DerogationsDeFamille>>,
  appEntries: readonly PorteuseDePortes[],
): Record<string, DerogationsDeFamille> {
  const resultat: Record<string, DerogationsDeFamille> = {};
  const poser = (famille: string, clef: string, niveau: NiveauAcces): void => {
    const derogations = resultat[famille] ?? {};
    derogations[clef] = niveau;
    resultat[famille] = derogations;
  };

  /* 1. Les derogations explicites, recopiees telles quelles. */
  for (const [famille, derogations] of Object.entries(portesExplicites)) {
    for (const [clef, niveau] of Object.entries(derogations)) {
      if (niveau === undefined) continue;
      poser(famille, clef, niveau);
    }
  }

  /* 2 et 3. Les choix par app, traduits en portes. */
  for (const entry of appEntries) {
    const choix = apps[entry.id];
    if (choix === undefined) continue;
    for (const porte of entry.portes ?? []) {
      const clef = porte.type == null || porte.type === '' ? CLE_FAMILLE : porte.type;
      if (portesExplicites[porte.famille]?.[clef] !== undefined) continue;
      const deja = resultat[porte.famille]?.[clef];
      poser(porte.famille, clef, deja === undefined ? choix : plusPermissif(deja, choix));
    }
  }

  return resultat;
}

/**
 * LES PORTES SUR LESQUELLES DEUX APPS NE DISENT PAS LA MEME CHOSE, pour que le
 * panneau les NOMME au lieu de laisser l'utilisateur decouvrir le resultat.
 *
 * Rendues en clair (`assets/materiel`, `contacts`), triees, sans doublon. Une
 * porte couverte par une derogation explicite n'est jamais un conflit : la
 * derogation tranche avant les apps.
 */
export function portesEnConflit(
  apps: Readonly<Record<string, NiveauAcces>>,
  appEntries: readonly PorteuseDePortes[],
  portesExplicites: Readonly<Record<string, DerogationsDeFamille>> = {},
): string[] {
  const vues = new Map<string, NiveauAcces>();
  const conflits = new Set<string>();
  for (const entry of appEntries) {
    const choix = apps[entry.id];
    if (choix === undefined) continue;
    for (const porte of entry.portes ?? []) {
      const clef = porte.type == null || porte.type === '' ? CLE_FAMILLE : porte.type;
      if (portesExplicites[porte.famille]?.[clef] !== undefined) continue;
      const nom = clef === CLE_FAMILLE ? porte.famille : `${porte.famille}/${clef}`;
      const deja = vues.get(nom);
      if (deja !== undefined && deja !== choix) conflits.add(nom);
      else if (deja === undefined) vues.set(nom, choix);
    }
  }
  return [...conflits].sort();
}

/**
 * LES DEROGATIONS QUI NE S'EXPLIQUENT PAS PAR LES CHOIX D'APP, c'est-a-dire
 * celles qui ont ete posees A LA MAIN sur une porte.
 *
 * POURQUOI CETTE FONCTION EXISTE, ET C'EST LE PIEGE DE TOUT L'ECRAN : ce qui
 * est ENREGISTRE, ce sont les portes, choix d'app compris (les rules ne lisent
 * que ca). A la reouverture d'une fiche, une porte posee par un choix d'app est
 * donc indiscernable d'une derogation manuelle, et `deriverPortes` la ferait
 * primer sur ce meme choix d'app : passer une app de `read` a `write` n'aurait
 * alors plus aucun effet, en silence.
 *
 * On rejoue donc la derivation a vide et on RETIRE ce qu'elle explique
 * exactement. Ce qui reste est manuel, et prime a l'enregistrement suivant.
 */
export function portesExplicitesDe(
  apps: Readonly<Record<string, NiveauAcces>>,
  portes: Readonly<Record<string, DerogationsDeFamille>>,
  appEntries: readonly PorteuseDePortes[],
): Record<string, DerogationsDeFamille> {
  const derivees = deriverPortes(apps, {}, appEntries);
  const manuelles: Record<string, DerogationsDeFamille> = {};
  for (const [famille, derogations] of Object.entries(portes)) {
    for (const [clef, niveau] of Object.entries(derogations)) {
      if (niveau === undefined) continue;
      if (derivees[famille]?.[clef] === niveau) continue;
      const garde = manuelles[famille] ?? {};
      garde[clef] = niveau;
      manuelles[famille] = garde;
    }
  }
  return manuelles;
}
